import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import "./TablePaginationControls.css";

export default function TablePaginationControls({
  table,
  idPrefix = "table",
  elementIds = {},
  pageSizeOptions = [5, 10, 20, 25, 50, 100],
  showFirstLast = true,
  paginationMode = "full",
  className = "",
  compact = false,
}) {
  const pageCount = table ? Math.max(1, table.getPageCount()) : 1;
  const currentPage = table
    ? Math.min(table.getState().pagination.pageIndex + 1, pageCount)
    : 1;

  const [inputValue, setInputValue] = useState(String(currentPage));

  useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  if (!table) return null;

  const resolveElementId = (key, fallback) => elementIds[key] || fallback;
  const isMinimal = paginationMode === "minimal";

  const handlePageInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handlePageInputCommit = () => {
    const parsed = parseInt(inputValue, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= pageCount) {
      table.setPageIndex(parsed - 1);
    } else {
      setInputValue(String(currentPage));
    }
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === "Enter") {
      handlePageInputCommit();
    }
  };

  return (
    <div
      id={resolveElementId("pagination", `${idPrefix}-pagination`)}
      className={`table-pagination-controls ${isMinimal ? "table-pagination-controls-minimal" : ""} ${compact ? "table-pagination-controls-compact" : ""} ${className}`.trim()}
    >
      {!isMinimal && showFirstLast && (
        <Button
          id={resolveElementId("paginationFirst", `${idPrefix}-pagination-first`)}
          className="table-pagination-nav-btn table-pagination-first"
          size="sm"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          First
        </Button>
      )}

      <Button
        id={resolveElementId("paginationPrev", `${idPrefix}-pagination-prev`)}
        className="table-pagination-nav-btn table-pagination-prev"
        size="sm"
        onClick={() => table.previousPage()}
        disabled={!table.getCanPreviousPage()}
      >
        Previous
      </Button>

      <span
        id={resolveElementId("paginationStatus", `${idPrefix}-pagination-status`)}
        className="table-pagination-status"
      >
        Page{" "}
        <input
          id={resolveElementId("paginationPageInput", `${idPrefix}-pagination-page-input`)}
          className="table-pagination-page-input"
          type="number"
          min={1}
          max={pageCount}
          value={inputValue}
          onChange={handlePageInputChange}
          onBlur={handlePageInputCommit}
          onKeyDown={handlePageInputKeyDown}
        />{" "}
        of <strong>{pageCount}</strong>
      </span>

      <Button
        id={resolveElementId("paginationNext", `${idPrefix}-pagination-next`)}
        className="table-pagination-nav-btn table-pagination-next"
        size="sm"
        onClick={() => table.nextPage()}
        disabled={!table.getCanNextPage()}
      >
        Next
      </Button>

      {!isMinimal && showFirstLast && (
        <Button
          id={resolveElementId("paginationLast", `${idPrefix}-pagination-last`)}
          className="table-pagination-nav-btn table-pagination-last"
          size="sm"
          onClick={() => table.setPageIndex(pageCount - 1)}
          disabled={!table.getCanNextPage()}
        >
          Last
        </Button>
      )}

      {!isMinimal && (
        <Form.Select
          id={resolveElementId("pageSizeInput", `${idPrefix}-page-size-input`)}
          className="table-pagination-page-size"
          size="sm"
          value={table.getState().pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
        >
          {pageSizeOptions.map((s) => (
            <option key={s} value={s}>{s} rows</option>
          ))}
        </Form.Select>
      )}
    </div>
  );
}
